import React, { useState } from 'react';
import { IStackTokens, Stack, Text } from '@fluentui/react';
import {
    getTimelineProgress,
    TimelineDependency,
    TimelineItem,
} from '../../../../models/Timeline';
import { FlightDataDisplay } from './FlightDataDisplay';
import { StatusBadge } from '../../../../../Common/components/StatusBadge';
import { getSLA } from '../../../../util/SlaUtils';
import { getTransferMechanism } from '../../../../../Common/sovereignClouds/sovereignCloudData';
import './ReplicationTimeline.css';

interface ICurrentReplicationTimeline {
    currentFlightTimeline: TimelineItem[] | undefined;
    currentCloudName?: string;
    dependencies?: TimelineDependency[];
    onDependencyClick?: (uniqueIdentifier: string) => void;
}

export const CurrentReplicationTimeline: React.FC<ICurrentReplicationTimeline> =
    (props: ICurrentReplicationTimeline) => {
        const [hoveredSLA, setHoveredSLA] = useState<
            'SRM' | 'CTS' | 'Customer' | null
        >(null);
        const stackTokens: IStackTokens = { childrenGap: 0 };
        const sectionStackTokens: IStackTokens = { childrenGap: 20 };
        const transferMechanism = getTransferMechanism(props.currentCloudName);
        const visibleTimeline =
            props.currentFlightTimeline?.filter((item) => item.shouldDisplay) ??
            [];
        const { inProgressIndex, endedAtIndex } =
            getTimelineProgress(visibleTimeline);
        const skippedIndexes = new Set(
            visibleTimeline
                .map((item, index) =>
                    item.status === 'NotStarted' &&
                    visibleTimeline
                        .slice(index + 1)
                        .some(
                            (laterItem) =>
                                !laterItem.isProjected &&
                                (Boolean(laterItem.timestamp) ||
                                    laterItem.status !== 'NotStarted')
                        )
                        ? index
                        : -1
                )
                .filter((index) => index !== -1)
        );

        const getStageType = (stageName: string) => {
            switch (stageName) {
                case 'Build Validated':
                case 'Uploaded to Hydration Cache':
                case 'Uploaded to Storage':
                case 'Dependency Checks Completed':
                case 'Submitted for Approval':
                case 'Submitted to Deployer':
                    return 'SRM';
                case 'Deployment Arrived':
                case 'Build Arrived':
                case 'Transfer Completed':
                    return 'CTS';
                default:
                    return 'Customer';
            }
        };

        return (
            <div
                className="CurrentReplicationTimeline-root"
                style={{ marginTop: 20 }}
            >
                <Stack tokens={sectionStackTokens}>
                    <Stack
                        horizontal
                        tokens={{ childrenGap: 12 }}
                        style={{ alignItems: 'center' }}
                    >
                        <Text
                            variant={'mediumPlus'}
                            style={{ lineHeight: '28px', fontWeight: 600 }}
                        >
                            Release progress
                        </Text>
                        <Stack
                            horizontal
                            tokens={{ childrenGap: 8 }}
                            style={{ alignItems: 'center' }}
                        >
                            {transferMechanism !== 'None' && (
                                <StatusBadge
                                    text={`${
                                        transferMechanism ?? 'CTS'
                                    } SLA: ${getSLA(
                                        'CTS',
                                        props.currentCloudName
                                    )}`}
                                    variant={'neutral'}
                                    onMouseEnter={() => setHoveredSLA('CTS')}
                                    onMouseLeave={() => setHoveredSLA(null)}
                                />
                            )}
                            <StatusBadge
                                text={`SRM SLA: ${getSLA(
                                    'SRM',
                                    props.currentCloudName
                                )}`}
                                variant={'neutral'}
                                onMouseEnter={() => setHoveredSLA('SRM')}
                                onMouseLeave={() => setHoveredSLA(null)}
                            />
                        </Stack>
                    </Stack>
                    <Stack tokens={stackTokens}>
                        {visibleTimeline.length > 0 &&
                            visibleTimeline.map((marker, index) => (
                                <FlightDataDisplay
                                    key={`${marker.name}-${index}`}
                                    name={marker.name}
                                    cloud={marker.cloud}
                                    description={marker.description}
                                    pendingName={marker.pendingName}
                                    pendingDescription={
                                        marker.pendingDescription
                                    }
                                    timestamp={marker.timestamp}
                                    status={marker.status}
                                    statusDetails={marker.statusDetails}
                                    isProjected={marker.isProjected}
                                    isValid={marker.isValid}
                                    shouldDisplay={marker.shouldDisplay}
                                    index={index}
                                    inProgressIndex={inProgressIndex}
                                    previousTimestamp={
                                        index > 0
                                            ? visibleTimeline[index - 1]
                                                  .timestamp
                                            : undefined
                                    }
                                    hoveredSLA={hoveredSLA}
                                    stageType={getStageType(marker.name)}
                                    isLast={
                                        index === visibleTimeline.length - 1
                                    }
                                    willNotRun={
                                        (endedAtIndex !== -1 &&
                                            index > endedAtIndex) ||
                                        skippedIndexes.has(index)
                                    }
                                    allSubsequentItemsLackTimestamp={visibleTimeline
                                        .slice(index + 1)
                                        .every((item) => !item.timestamp)}
                                    dependencies={
                                        marker.name.startsWith(
                                            'Dependency Checks'
                                        ) ||
                                        marker.name.startsWith('Dependencies ')
                                            ? props.dependencies
                                            : undefined
                                    }
                                    onDependencyClick={props.onDependencyClick}
                                />
                            ))}
                    </Stack>
                </Stack>
            </div>
        );
    };
